import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { TenantProfile } from "../../../../api/graphql";
import { TenantProfileListStore } from "../../store";
import { TenantSetupGuide } from "../common/TenantSetupGuide";
import { publisherDisplayId } from "../../../publisher/store/PublisherCommonStore";
import { countryDisplayId } from "../../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../../provinceState/store/ProvinceStateCommonStore";
import { vatStatusDisplayId } from "../../../vatStatus/store/VatStatusCommonStore";
import { grossReceiptsStatusDisplayId } from "../../../grossReceiptsStatus/store/GrossReceiptsStatusCommonStore";
import { currencyDisplayId } from "../../../currency/store/CurrencyCommonStore";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: TenantProfileListStore };

export const TenantProfileListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<TenantProfile>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: TenantProfile, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: TenantProfile, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: TenantProfile) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: TenantProfile, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<TenantProfile>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "personType") {
          return {
            ...col,
            id: "personType",
            accessorFn: (row: TenantProfile) => {
              const v = (row as any).personType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.PersonTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "ownPublisher") {
          return {
            ...col,
            id: "ownPublisher",
            accessorFn: (row: TenantProfile) =>
              row.ownPublisher
                ? publisherDisplayId(row.ownPublisher, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "country") {
          return {
            ...col,
            id: "country",
            accessorFn: (row: TenantProfile) =>
              row.country ? countryDisplayId(row.country, langStore) : "-",
          };
        }
        if (col.accessorKey === "province") {
          return {
            ...col,
            id: "province",
            accessorFn: (row: TenantProfile) =>
              row.province
                ? provinceStateDisplayId(row.province, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "vatStatus") {
          return {
            ...col,
            id: "vatStatus",
            accessorFn: (row: TenantProfile) =>
              row.vatStatus
                ? vatStatusDisplayId(row.vatStatus, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "grossReceiptsStatus") {
          return {
            ...col,
            id: "grossReceiptsStatus",
            accessorFn: (row: TenantProfile) =>
              row.grossReceiptsStatus
                ? grossReceiptsStatusDisplayId(
                    row.grossReceiptsStatus,
                    langStore
                  )
                : "-",
          };
        }
        if (col.accessorKey === "billingCurrency") {
          return {
            ...col,
            id: "billingCurrency",
            accessorFn: (row: TenantProfile) =>
              row.billingCurrency
                ? currencyDisplayId(row.billingCurrency, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "printerCountry") {
          return {
            ...col,
            id: "printerCountry",
            accessorFn: (row: TenantProfile) =>
              row.printerCountry
                ? countryDisplayId(row.printerCountry, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "printerProvince") {
          return {
            ...col,
            id: "printerProvince",
            accessorFn: (row: TenantProfile) =>
              row.printerProvince
                ? provinceStateDisplayId(row.printerProvince, langStore)
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.tenantProfileCurrentPage || [];

    return (
      <div id={"ListOfTenantProfile"}>
        <TenantSetupGuide />
        <div id={"StartOfListingOfTenantProfile"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
